import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, AlertTriangle, Phone, MessageCircle } from "lucide-react";
import axios from "axios";
import { CATEGORIES, CONTACT } from "@/data/services";
import { TID } from "@/constants/testIds/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...CATEGORIES.flatMap((c) => c.items.map((it) => `${c.label} — ${it.title}`)),
  "Pack Starter",
  "Pack Growth",
  "Pack Empire",
  "Autre / Sur mesure",
];

const Contact = forwardRef(function Contact(_, ref) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [state, setState] = useState({ loading: false, ok: null, err: null });

  useImperativeHandle(ref, () => ({
    setService: (s) => setForm((f) => ({ ...f, service: s })),
  }));

  useEffect(() => {
    if (state.ok) {
      const t = setTimeout(() => setState((s) => ({ ...s, ok: null })), 6000);
      return () => clearTimeout(t);
    }
  }, [state.ok]);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: null, err: null });
    try {
      await axios.post(`${API}/orders`, form);
      setState({ loading: false, ok: true, err: null });
      setForm({ name: "", phone: "", service: form.service, message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Une erreur est survenue. Réessayez.";
      setState({ loading: false, ok: false, err: detail });
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contact.section}
      className="relative py-28 md:py-36 px-6 md:px-10 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.4em] text-white/50 mb-4 flex items-center gap-3">
            <span className="dot-gold" />
            Passer commande
          </div>
          <h2 className="font-display text-5xl md:text-7xl tracking-tighter text-white leading-[0.9]">
            Prêt à <span className="text-gold-gradient italic">décoller</span> ?
          </h2>
          <p className="text-white/60 mt-6 text-lg leading-relaxed max-w-md">
            Remplissez le formulaire — nous vous rappelons sous 2h ouvrées avec
            un devis clair et un plan d&apos;action.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`https://wa.me/${CONTACT.whatsappRaw}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between p-5 rounded-xl border border-white/10 hover:border-[#25D366]/50 bg-[#111] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-[#25D366]/15 text-[#25D366]">
                  <MessageCircle size={18} />
                </div>
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-[0.3em]">
                    WhatsApp
                  </div>
                  <div className="text-white font-medium">{CONTACT.whatsapp}</div>
                </div>
              </div>
              <span className="text-white/40 group-hover:text-[#d4af37] transition-colors">
                →
              </span>
            </a>
            <a
              href={`tel:${CONTACT.infolineRaw}`}
              className="flex items-center justify-between p-5 rounded-xl border border-white/10 hover:border-[#d4af37]/50 bg-[#111] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-[#d4af37]/15 text-[#d4af37]">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-[0.3em]">
                    Infoline
                  </div>
                  <div className="text-white font-medium">{CONTACT.infoline}</div>
                </div>
              </div>
              <span className="text-white/40 group-hover:text-[#d4af37] transition-colors">
                →
              </span>
            </a>
          </div>
        </div>

        <div className="md:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={submit}
            data-testid={TID.contact.form}
            className="glass rounded-2xl p-8 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  Nom complet
                </span>
                <input
                  required
                  data-testid={TID.contact.inputName}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Aïcha Koffi"
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-white/25"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  Téléphone / WhatsApp
                </span>
                <input
                  required
                  data-testid={TID.contact.inputPhone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+225 07 00 00 00 00"
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-white/25"
                />
              </label>
            </div>

            <label className="block mt-5">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                Service souhaité
              </span>
              <select
                data-testid={TID.contact.selectService}
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3.5 text-white"
              >
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="bg-[#0a0a0a]">
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mt-5">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                Message (optionnel)
              </span>
              <textarea
                data-testid={TID.contact.inputMessage}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Parlez-nous de votre business, votre cible, vos objectifs..."
                className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-white/25 resize-none"
              />
            </label>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-white/50 text-xs leading-relaxed max-w-sm">
                En envoyant, vous acceptez d&apos;être recontacté par notre équipe.
              </div>
              <button
                type="submit"
                disabled={state.loading}
                data-testid={TID.contact.submitBtn}
                className="btn-gold px-8 py-4 rounded-full inline-flex items-center gap-2 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {state.loading ? "Envoi..." : "Envoyer ma demande"}
                <Send size={16} />
              </button>
            </div>

            {state.ok && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={TID.contact.successMsg}
                className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#f5e08f]"
              >
                <CheckCircle2 size={18} className="mt-0.5" />
                <div className="text-sm">
                  Demande reçue ! Nous vous contactons très vite sur WhatsApp.
                </div>
              </motion.div>
            )}
            {state.err && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={TID.contact.errorMsg}
                className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300"
              >
                <AlertTriangle size={18} className="mt-0.5" />
                <div className="text-sm">{state.err}</div>
              </motion.div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
});

export default Contact;
